import { useState, useRef, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight, Pencil } from 'lucide-react'
import type { AskUserQuestion } from '@/types'

interface QuestionCardProps {
  questions: AskUserQuestion[]
  onSubmit: (answers: Record<number, string[]>) => void
  answered?: boolean
}

export function QuestionCard({ questions, onSubmit, answered }: QuestionCardProps) {
  const [page, setPage] = useState(0)
  const [answers, setAnswers] = useState<Record<number, string[]>>({})
  const [freetext, setFreetext] = useState<Record<number, string>>({})
  const [submitted, setSubmitted] = useState(answered ?? false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = cardRef.current
    if (!el) return
    el.style.opacity = '0'
    el.style.transform = 'translateY(12px)'
    el.style.transition = 'opacity 0.35s ease, transform 0.35s ease'
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
      })
    })
  }, [])

  const q = questions[page]
  const totalPages = questions.length

  function getOptionLabel(option: string) {
    if (option.includes('Bygg dette') || option.includes('Build this')) {
      return 'Ja'
    }
    return option
  }

  function toggleOption(option: string) {
    const current = answers[page] ?? []
    if (q.selection_type === 'single') {
      setAnswers(prev => ({ ...prev, [page]: [option] }))
    } else if (current.includes(option)) {
      setAnswers(prev => ({ ...prev, [page]: current.filter(o => o !== option) }))
    } else {
      setAnswers(prev => ({ ...prev, [page]: [...current, option] }))
    }
  }

  function handleSkip() {
    if (page < totalPages - 1) {
      setPage(p => p + 1)
    } else {
      handleSubmit()
    }
  }

  function handleNext() {
    if (page < totalPages - 1) {
      setPage(p => p + 1)
    } else {
      handleSubmit()
    }
  }

  function handleSubmit() {
    const merged: Record<number, string[]> = { ...answers }
    for (const [idx, text] of Object.entries(freetext)) {
      const i = Number(idx)
      if (text.trim()) {
        merged[i] = [...(merged[i] ?? []), text.trim()]
      }
    }
    setSubmitted(true)
    onSubmit(merged)
  }

  const currentAnswers = answers[page] ?? []
  const currentFreetext = freetext[page] ?? ''
  const hasAnswer = currentAnswers.length > 0 || currentFreetext.trim().length > 0

  if (submitted) {
    return null
  }

  return (
    <div ref={cardRef} className="question-card" role="dialog" aria-label="Spørsmål fra Sine">
      <div className="question-card-header">
        <span className="question-card-title">{q.title}</span>
        <div className="question-card-nav">
          {totalPages > 1 && (
            <>
              <button
                className="question-card-nav-btn"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                aria-label="Forrige spørsmål"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="question-card-page">{page + 1} av {totalPages}</span>
              <button
                className="question-card-nav-btn"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
                aria-label="Neste spørsmål"
              >
                <ChevronRight size={14} />
              </button>
            </>
          )}
          <button
            className="question-card-nav-btn question-card-close"
            onClick={handleSubmit}
            aria-label="Lukk"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      <div className="question-card-options">
        {q.options.map((option, i) => {
          const selected = currentAnswers.includes(option)
          return (
            <button
              key={option}
              className={`question-card-option${selected ? ' selected' : ''}`}
              onClick={() => toggleOption(option)}
              aria-pressed={selected}
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleOption(option)
                } else if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  const btns = cardRef.current?.querySelectorAll<HTMLButtonElement>('.question-card-option')
                  if (btns) btns[Math.min(i + 1, btns.length - 1)]?.focus()
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  const btns = cardRef.current?.querySelectorAll<HTMLButtonElement>('.question-card-option')
                  if (btns) btns[Math.max(i - 1, 0)]?.focus()
                } else if (e.key === String(i + 1)) {
                  e.preventDefault()
                  toggleOption(option)
                }
              }}
            >
              <span className="question-card-num">{i + 1}</span>
              <span className="question-card-option-text">{getOptionLabel(option)}</span>
              {selected && q.selection_type === 'single' && (
                <ChevronRight size={14} className="question-card-option-arrow" />
              )}
            </button>
          )
        })}
      </div>

      <div className="question-card-footer">
        <div className="question-card-freetext-wrap">
          <Pencil size={13} className="question-card-freetext-icon" />
          <input
            type="text"
            className="question-card-freetext"
            placeholder="Noe annet..."
            value={currentFreetext}
            onChange={e => setFreetext(prev => ({ ...prev, [page]: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') handleNext() }}
          />
        </div>
        <div className="question-card-footer-actions">
          <button className="question-card-skip-btn" onClick={handleSkip}>
            Skip
          </button>
          {hasAnswer && (
            <button className="question-card-next-btn" onClick={handleNext}>
              {page < totalPages - 1 ? 'Neste →' : 'Send →'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
